/**
 * Decides how the private support video player reacts to a media error.
 *
 * The player streams from a short-lived storage address. When that address
 * expires mid-stream the next range request is rejected and the media element
 * raises `error`. The browser cannot see why: the `<video>` element carries no
 * `crossorigin` attribute, so `MediaError` exposes no status and no message.
 *
 * Comparing the server-issued expiry against `Date.now()` is not a reliable way
 * to tell expiry apart from other failures either, because the two clocks are
 * independent and do drift. So the player does not try to name the cause. It
 * assumes the likely one, fetches a fresh address, and resumes — and only
 * reports a failure when that does not work.
 */

/**
 * How long a renewal must survive before another error is treated as a fresh
 * expiry rather than a symptom of a video that is simply broken. Long enough to
 * outlast the reload itself, far shorter than any sane token lifetime, so a
 * genuinely long viewing session can renew as many times as it needs to.
 */
export const SUPPORT_VIDEO_RENEW_COOLDOWN_MS = 10000;

/**
 * Returns whether a media error should trigger an automatic renewal.
 *
 * @param lastRenewedAt Epoch ms of the last renewal attempt, or 0 if none.
 * @param now Current epoch ms.
 */
export function shouldRenewSupportVideo(
  lastRenewedAt: number,
  now: number
): boolean {
  if (lastRenewedAt <= 0) return true;
  return now - lastRenewedAt >= SUPPORT_VIDEO_RENEW_COOLDOWN_MS;
}

/**
 * Folds a `timeupdate` reading into the last known playback position.
 *
 * Reading `currentTime` off the element at the moment renewal starts is not
 * safe: a media element that has just errored may have already torn down its
 * decode pipeline and reset `currentTime` to 0, and it reports `NaN` before any
 * metadata is loaded. Resuming from either value restarts the video from the
 * beginning without saying so. Tracking the position while playback is healthy
 * and ignoring the non-positive readings keeps the last real position.
 *
 * @param lastKnownPosition Seconds, as previously tracked. 0 when none.
 * @param currentTime The element's `currentTime` for this event.
 */
export function trackSupportVideoPosition(
  lastKnownPosition: number,
  currentTime: number
): number {
  if (!Number.isFinite(currentTime) || currentTime <= 0) {
    return lastKnownPosition;
  }
  return currentTime;
}

/** Shown when playback was interrupted and could not be picked up again. */
export const SUPPORT_VIDEO_INTERRUPTED_MESSAGE =
  'Playback stopped and could not be resumed.';

/** Shown when the server will not issue an address for this video any more. */
export const SUPPORT_VIDEO_WITHDRAWN_MESSAGE =
  'This video is no longer available.';

/** Shown when obtaining an address failed for a reason that may not recur. */
export const SUPPORT_VIDEO_UNAVAILABLE_MESSAGE =
  'This video could not be loaded right now.';

/**
 * What to tell the viewer when the playback endpoint refuses or fails, and
 * whether offering a retry is honest.
 */
export interface SupportVideoFailure {
  /** HTTP status, or null when the request never got a response. */
  status: number | null;
  message: string;
  canRetry: boolean;
}

/**
 * Classifies a failure to obtain a playback address.
 *
 * The endpoint deliberately answers an unauthorized, unknown, or withdrawn
 * request identically — a 404 with no detail — so that it cannot be used to
 * probe for documents. That makes 404 the one status worth acting on: whatever
 * the underlying reason, this viewer is never going to be given this video, and
 * inviting them to retry would be a lie. Everything else (network failure, 5xx,
 * an expired session) may well succeed on a second attempt.
 *
 * The address itself is a credential, so nothing here reads or returns the
 * response body — only the status.
 */
export function describeSupportVideoFailure(
  error: unknown
): SupportVideoFailure {
  const status =
    (error as { response?: { status?: number } })?.response?.status ?? null;

  if (status === 404) {
    return {
      status,
      message: SUPPORT_VIDEO_WITHDRAWN_MESSAGE,
      canRetry: false,
    };
  }

  return {
    status,
    message: SUPPORT_VIDEO_UNAVAILABLE_MESSAGE,
    canRetry: true,
  };
}
